import React from 'react';
import ReactDOM from 'react-dom';
import Icon from '../AppIcon';
import { useTranslation } from 'react-i18next';

/**
 * The menu that drops out of a column header.
 *
 * Sorting and filtering are two different questions about one column — "which
 * order?" and "which of these?" — so they share one surface, opened by clicking
 * the header. The alternative (a separate funnel icon beside the label) puts two
 * hit targets in a 44px header cell and makes the smaller one the important one.
 *
 * Rendered through a PORTAL, deliberately. Every table in this app sits inside
 * `overflow-x-auto`, and `<th>` establishes no containing block worth anchoring
 * to, so a dropdown positioned inside the cell gets clipped by the scroll
 * container the moment it is taller than the header row. Fixed positioning
 * against the trigger's measured rect is the only version that survives a
 * horizontally scrolled table.
 *
 * Values are counted against the OTHER columns' filters but not this column's
 * own — the same rule the conference facets use. It's what lets you see that
 * ticking a second status would add eleven rows before you tick it.
 */

const MENU_W = 248;

export default function ColumnFilterMenu({
  anchorEl,
  onClose,
  label,
  options,            // [{ value, label, count }]
  selected = [],
  onToggle,
  onOnly,
  onClear,
  onSelectAll,
  sort,               // { key, direction } | null
  field,
  onSort,
  sortable = true,
}) {
  const { t } = useTranslation('common');
  const ref = React.useRef(null);
  const [query, setQuery] = React.useState('');
  const [pos, setPos] = React.useState(null);

  // Measure once mounted, and again if the page moves under us.
  React.useLayoutEffect(() => {
    if (!anchorEl) return;
    const place = () => {
      const r = anchorEl.getBoundingClientRect();
      const rtl = document.documentElement.dir === 'rtl';
      // Align the menu's inline-start edge with the header's, then pull it back
      // inside the viewport if it would overflow.
      let left = rtl ? r.right - MENU_W : r.left;
      left = Math.min(Math.max(8, left), window.innerWidth - MENU_W - 8);
      setPos({ top: r.bottom + 4, left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorEl]);

  React.useEffect(() => {
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || anchorEl?.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchorEl, onClose]);

  if (!pos) return null;

  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => String(o.label).toLowerCase().includes(q)) : options;
  const allSelected = selected.length === 0 || selected.length === options.length;

  return ReactDOM.createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_W }}
      className="z-dropdown bg-popover border border-border shadow-elevation-3 motion-rise"
    >
      {/* Name the column. Without this the menu is an anonymous list of values
          and you cannot tell, three columns in, which one you are editing. */}
      <div className="px-3 py-2 border-b border-border">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t('table.filterColumn', 'Column')}
        </p>
        <p className="text-[13px] font-semibold text-foreground truncate">{label}</p>
      </div>

      {sortable && (
        <div className="py-1 border-b border-border">
          {[
            { dir: 'asc', icon: 'ArrowUp', text: t('table.sortAsc', 'Sort ascending') },
            { dir: 'desc', icon: 'ArrowDown', text: t('table.sortDesc', 'Sort descending') },
          ].map((s) => {
            const on = sort?.key === field && sort?.direction === s.dir;
            return (
              <button
                key={s.dir}
                type="button"
                onClick={() => { onSort?.(field, s.dir); onClose?.(); }}
                className={`w-full flex items-center gap-2 px-3 h-8 text-[13px] text-start transition-colors focus-ring ${
                  on ? 'text-primary font-medium bg-primary-subtle' : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={s.icon} size={13} className="flex-shrink-0" />
                <span className="flex-1">{s.text}</span>
                {on && <Icon name="Check" size={13} />}
              </button>
            );
          })}
        </div>
      )}

      {options.length === 0 ? (
        <p className="px-3 py-4 text-[12px] text-muted-foreground">
          {t('table.noValues', 'Nothing to filter on in this column.')}
        </p>
      ) : (
        <>
          {/* A search box only once the list stops being scannable. */}
          {options.length > 8 && (
            <div className="p-2 border-b border-border">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('table.filterSearch', 'Search values…')}
                className="w-full h-8 px-2 bg-card border border-input text-[13px] text-foreground placeholder:text-muted-foreground focus-ring"
              />
            </div>
          )}

          <div className="max-h-60 overflow-y-auto scrollbar-thin py-1">
            {shown.length === 0 && (
              <p className="px-3 py-3 text-[12px] text-muted-foreground">
                {t('table.noMatches', 'No values match.')}
              </p>
            )}
            {shown.map((o) => {
              // No selection means "everything", so every box reads as ticked —
              // an unfiltered column showing all boxes empty implies it is
              // hiding all rows, which is the opposite of what it is doing.
              const checked = selected.length === 0 || selected.includes(o.value);
              return (
                <div key={String(o.value)} className="group flex items-center gap-2 px-3 h-8 hover:bg-muted">
                  <input
                    id={`f-${field}-${o.value}`}
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle?.(o.value)}
                    className="w-3.5 h-3.5 flex-shrink-0 accent-primary cursor-pointer focus-ring"
                  />
                  <label
                    htmlFor={`f-${field}-${o.value}`}
                    className="flex-1 min-w-0 truncate text-[13px] text-foreground cursor-pointer"
                    title={o.label}
                  >
                    {o.label}
                  </label>
                  {/* "Only" is the move people actually want most of the time,
                      and doing it with checkboxes is N-1 clicks. */}
                  <button
                    type="button"
                    onClick={() => onOnly?.(o.value)}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-[11px] text-primary underline underline-offset-2 focus-ring"
                  >
                    {t('table.only', 'only')}
                  </button>
                  <span className="font-mono text-[11px] tabular-nums text-muted-foreground" dir="ltr">
                    {o.count}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-2 px-3 h-9 border-t border-border">
            <button
              type="button"
              onClick={onSelectAll}
              disabled={allSelected}
              className="text-[12px] text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:opacity-45 disabled:cursor-not-allowed focus-ring"
            >
              {t('table.selectAll', 'Select all')}
            </button>
            <button
              type="button"
              onClick={onClear}
              disabled={selected.length === 0}
              className="text-[12px] text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:opacity-45 disabled:cursor-not-allowed focus-ring"
            >
              {t('table.clearColumn', 'Clear')}
            </button>
          </div>
        </>
      )}
    </div>,
    document.body
  );
}
